import './MatchOptionsDialog.less';

import React, { useCallback, useContext } from 'react';
import { useReframe } from 'nike-re-framejs';
import { DialogBody, DialogTitle, Docked } from '@/ui/dialogs/Dialog';
import PopupNg from '@/ui/PopupNg';
import BellActiveIcon from '@/icons/BellActiveIcon';
import BellIcon from '@/icons/BellIcon';
import { DialogRow, DialogRowButton, DialogRowIcon, DialogRowText } from '@/ui/dialogs/DialogRow';
import SmallLoader from '@/ui/SmallLoader';
import i18n from '@i18n';
import { fireGAEvent } from '@/analytics';
import { disabledGray, softBlue } from '@/nikeone/NikeOneCommon';
import useMatchNotification from '@/notifications/components/useMatchNotification';
import betInfo from '@/common/betInfo';
import { NIKE1_MATCH, TURBO_LIVE_MATCH_DETAIL } from '@/routing/routes';
import { navigate } from '@/routing';
import { CURRENT_PAGE } from 'nike-ui/src/common/routing';
import { trackingPropsForSportEvent } from '@/common/tracking';
import SportEventContext from 'nike-ui/src/common/SportEventContext';
import { sportEventUrlDescription } from 'nike-ui/src/common/sportEvents';
import { nike1LiveEventOptionsInfo$, nike1PrematchEventOptionsInfo$ } from '@/matchOptions/subs';
import { SvgIcon } from 'nike-ui/src/icons/SvgIcon';
import { getSportNameById } from 'nike-ui/src/common/utils/utils';

export const LiveMatchOptionsDialog = function({ eventId, pureLive, sportEvent }) {
    const derefSub = useReframe();
    const info = derefSub(nike1LiveEventOptionsInfo$(eventId));
    if (!info) {
        return null;
    }
    return <MatchOptionsDialog info={info} pureLive={pureLive} sportEvent={sportEvent} />;
};

export function PrematchMatchOptionsDialog({ boxStore, eventId, betId, matchNote, sportEvent }) {
    const derefSub = useReframe();
    const info = derefSub(nike1PrematchEventOptionsInfo$(boxStore, eventId, betId));
    if (!info) {
        return null;
    }
    return <MatchOptionsDialog info={info} matchNote={matchNote} sportEvent={sportEvent} />;
}

export function MatchOptionsDialog({ info, pureLive, matchNote, sportEvent }) {
    const eventId = info.eventId;
    const openDetail = useCallback(e => openMatchDetail(e, eventId, pureLive, sportEvent), [
        eventId,
        pureLive,
        sportEvent
    ]);

    return (
        <Docked
            className="match-options-dialog"
            data-sport={info.sportName}
            data-tournament={info.tournamentName}
            data-game-state={info.type}
            data-participants={info.opponents.join(' - ')}
        >
            <DialogTitle>
                <Title opponents={info.opponents} onClick={openDetail} />
            </DialogTitle>
            <DialogBody>
                <Description info={info} onClick={openDetail} matchNote={matchNote} />
                <MatchDetailRow onClick={openDetail} sportEvent={sportEvent} />
                <NotificationsRow eventId={eventId} />
            </DialogBody>
        </Docked>
    );
}

export function PrematchMatchOptionsTooltip({ boxStore, eventId, betId }) {
    const derefSub = useReframe();
    const info = derefSub(nike1PrematchEventOptionsInfo$(boxStore, eventId, betId));
    if (!info) {
        return null;
    }
    return <MatchOptionsTooltip info={info} />;
}

export function LiveMatchOptionsTooltip({ eventId, pureLive }) {
    const derefSub = useReframe();
    const info = derefSub(['sub:p::nikeone.live.event.options-info', eventId]);
    if (!info) {
        return null;
    }
    return <MatchOptionsTooltip info={info} pureLive={pureLive} />;
}

export function MatchOptionsTooltip({ info, pureLive }) {
    const eventId = info.eventId;
    const sportEvent = useContext(SportEventContext);
    const openDetail = useCallback(e => openMatchDetail(e, eventId, pureLive, sportEvent), [
        eventId,
        pureLive,
        sportEvent
    ]);
    return (
        <>
            <MatchDetailRow onClick={openDetail} sportEvent={sportEvent} />
            <NotificationsRow eventId={eventId} />
        </>
    );
}
export function Title({ opponents, onClick }) {
    const home = opponents[0];
    const away = opponents[1];
    return (
        <h2 onClick={onClick}>
            {home}
            {away && <span className="separator"> {i18n('homeAway.vs.separator')} </span>}
            {away}
        </h2>
    );
}

export function Description({ info, onClick, matchNote }) {
    return (
        <p className="description" onClick={onClick}>
            {betInfo(info)}
            {matchNote ? (
                <>
                    <span>&nbsp;|</span>
                    <span className="dialog-match-note">{matchNote}</span>
                </>
            ) : null}
        </p>
    );
}

export function NotificationsRow({ eventId }) {
    const [supported, subscribed, notificationTypes, openPopup] = useMatchNotification(eventId);
    if (!supported) {
        return null;
    }
    if (!notificationTypes) {
        return <RowLoader />;
    }
    const disabled = !notificationTypes.length;
    const color = disabled ? disabledGray : softBlue;
    const clickHandler = e => {
        fireGAEvent(CURRENT_PAGE, 'Nastavit upozornenia na zapas', 'Zapas');
        openPopup(e);
    };
    return (
        <DialogRowButton onClick={clickHandler} disabled={disabled}>
            <DialogRowIcon>
                {subscribed ? (
                    <BellActiveIcon height={20} width={20} fill={color} />
                ) : (
                    <BellIcon height={20} width={20} fill={color} />
                )}
            </DialogRowIcon>
            <DialogRowText>
                <div className={subscribed ? 'item-badge' : ''} data-badge={subscribed}>
                    {i18n('notifications.match.enable')}
                </div>
            </DialogRowText>
        </DialogRowButton>
    );
}

export function MatchDetailRow({ onClick, sportEvent }) {
    return (
        <DialogRowButton onClick={onClick} {...trackingPropsForSportEvent(sportEvent)}>
            <DialogRowIcon className="match-arrow-icon flex-center fs-0 br-radius-circle">
                <SvgIcon imgId="live-back" sizeX="14" sizeY="14" className="match-arrow-svg" />
            </DialogRowIcon>
            <DialogRowText>{i18n('match.detailShow')}</DialogRowText>
        </DialogRowButton>
    );
}

function RowLoader() {
    return (
        <DialogRow>
            <SmallLoader />
        </DialogRow>
    );
}

export function openMatchDetail(event, eventId, pureLive, sportEvent) {
    const sportName = getSportNameById(sportEvent?.get('sportId'));
    event.preventDefault();
    event.stopPropagation();
    fireGAEvent(CURRENT_PAGE, 'Zobrazit detail zapasu', 'Zapas');

    !pureLive
        ? navigate(NIKE1_MATCH, {
              eventId,
              meta: sportEventUrlDescription(sportEvent.toJS())
          })
        : navigate(TURBO_LIVE_MATCH_DETAIL, {
              sportName: sportName,
              matchId: eventId
          });

    PopupNg.dispatcher.close();
}
